/** Explicit sign-in recovery for recognized policy/interaction failures only. */
import type { AuthProvider } from "./types.js";
import {
  claimsBindingsMatch,
  recordPendingClaimsChallenge,
  validateClaimsBinding,
  type PowerPlatformClaimsBinding,
} from "./claims-challenge-store.js";

/** Never fall back to another account, app, resource, or a header-supplied authority. */
export function snapshotClaimsBinding(provider: AuthProvider): PowerPlatformClaimsBinding | null {
  try {
    const binding = provider.getPowerPlatformClaimsBinding?.() ?? null;
    if (!validateClaimsBinding(binding)) return null;
    return {
      clientId: binding.clientId,
      resource: binding.resource,
      account: {
        homeAccountId: binding.account.homeAccountId,
        localAccountId: binding.account.localAccountId,
        tenantId: binding.account.tenantId,
        environment: binding.account.environment,
        ...(binding.account.username === undefined ? {} : { username: binding.account.username }),
      },
    };
  } catch {
    return null;
  }
}

export function parkClaimsForExplicitSignIn(
  provider: AuthProvider,
  before: PowerPlatformClaimsBinding | null,
  claims: string,
  context: string,
  error: unknown,
): boolean {
  // 53003 identifies a policy denial, not which policy or whether a fresh sign-in
  // can satisfy it. Storage/network failures are not reasons to prompt a user.
  const message = error instanceof Error ? error.message.slice(0, 8192) : "";
  if (!/\b(?:AADSTS53003|AADSTS50076|AADSTS50079|interaction_required)\b/.test(message))
    return false;
  const after = snapshotClaimsBinding(provider);
  if (!before || !after || !claimsBindingsMatch(before, after)) return false;
  return recordPendingClaimsChallenge(claims, context, before);
}
